import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit"; // Type-only import
import apiClient from "../../services/apiClient";
import axios from "axios";
import { setSentryUser, clearSentryUser } from "../../config/sentry";

// Define the User type based on what the backend /api/auth/me returns
export interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  userType: "individual" | "company";
  companyName?: string | null;
  siret?: string | null;
  isEmailVerified: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  subscriptionCancelAtPeriodEnd?: boolean; // True if subscription is set to cancel at period end
  subscriptionCancelledAt?: string | null; // Date when subscription was cancelled
  customerId?: string | null; // Stripe customer ID
  subscriptionId?: string | null; // Stripe subscription ID
  enabledFeatures?: string[]; // Fonctionnalités activées pour le plan de l'utilisateur
  createdAt: string;
  updatedAt: string;
  tokenExpiringWarning?: boolean;
}

// API error interface that matches the backend error structure
interface ApiError {
  success: boolean;
  message: string;
  error?: string; // Error code like "UNAUTHORIZED" or "INTERNAL_SERVER_ERROR"
  redirectTo?: string; // For redirection after authentication failures
}

// API success response for auth status check
interface AuthStatusResponse extends Partial<User> {
  success: boolean;
  isAuthenticated: boolean;
  id: string;
  email: string;
  message?: string; // Optional message from the server
  redirectTo?: string; // For redirection if needed
  subscriptionCancelAtPeriodEnd?: boolean;
  subscriptionCancelledAt?: string | null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorCode?: string | null; // To store error codes from the API
  redirectTo?: string | null; // To store redirection path from API
  tokenExpiringWarning?: boolean; // Flag to indicate token is about to expire
  // For OTP flow
  otpUserId: string | null;
  otpSent: boolean;
  // For subscription flow
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  errorCode: null,
  redirectTo: null,
  tokenExpiringWarning: false,
  otpUserId: null,
  otpSent: false,
};

// Async thunk to check auth status (on app load, etc)
export const checkAuthStatus = createAsyncThunk<
  User,
  void,
  { rejectValue: ApiError }
>("auth/checkAuthStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get("/api/status");
    const data = response.data as AuthStatusResponse;

    // Check for token expiring warning header
    const tokenExpiringWarning =
      response.headers["x-token-expiring-soon"] === "true";

    if (!data.success || !data.isAuthenticated) {
      return rejectWithValue({
        success: false,
        message: data.message || "Not authenticated",
        error: "UNAUTHORIZED",
        // redirectTo: data.redirectTo || "/login",
      });
    }

    // Extract user data from the response
    const userData: User = {
      id: data.id,
      email: data.email,
      firstName: data.firstName ?? null,
      lastName: data.lastName ?? null,
      phone: data.phone ?? null,
      address: data.address ?? null,
      city: data.city ?? null,
      postalCode: data.postalCode ?? null,
      userType: data.userType ?? "individual",
      companyName: data.companyName ?? null,
      siret: data.siret ?? null,
      isEmailVerified: data.isEmailVerified ?? false,
      subscriptionPlan: data.subscriptionPlan ?? null,
      subscriptionStatus: data.subscriptionStatus ?? null,
      subscriptionEndDate: data.subscriptionEndDate ?? null,
      subscriptionCancelAtPeriodEnd:
        data.subscriptionCancelAtPeriodEnd ?? false,
      subscriptionCancelledAt: data.subscriptionCancelledAt ?? null,
      customerId: data.customerId ?? null,
      subscriptionId: data.subscriptionId ?? null,
      createdAt: data.createdAt ?? new Date().toISOString(),
      updatedAt: data.updatedAt ?? new Date().toISOString(),
      tokenExpiringWarning: tokenExpiringWarning,
      enabledFeatures: data.enabledFeatures ?? [],
    };

    return userData;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      // Include any redirection paths from the API
      const errorData = error.response.data as ApiError;
      return rejectWithValue({
        ...errorData,
        redirectTo: errorData.redirectTo || "/login",
      });
    }
    return rejectWithValue({
      success: false,
      message: "Failed to check authentication status",
      error: "NETWORK_ERROR",
      redirectTo: "/login",
    });
  }
});

// Async thunk to fetch current user's detailed profile
export const fetchCurrentUser = createAsyncThunk<
  User,
  void,
  { rejectValue: ApiError }
>("auth/fetchCurrentUser", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get("/api/auth/me");
    const userData = response.data as User;
    // The response is now the sanitized user data directly
    return userData;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return rejectWithValue(error.response.data as ApiError);
    }
    return rejectWithValue({
      success: false,
      message: "Failed to fetch user profile",
      error: "NETWORK_ERROR",
    });
  }
});

// Request OTP for login/registration
export const requestOtp = createAsyncThunk<
  { message: string; userId: string },
  {
    email: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    type: "register" | "login";
  },
  { rejectValue: ApiError }
>("auth/requestOtp", async (payload, { rejectWithValue }) => {
  const endpoint =
    payload.type === "register"
      ? "/api/auth/register/request-otp"
      : "/api/auth/login/verify-credentials";
  try {
    const response = await apiClient.post(endpoint, payload);
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return rejectWithValue(error.response.data as ApiError);
    }
    return rejectWithValue({
      success: false,
      message: `${payload.type === "register" ? "Registration" : "Login"} OTP request failed`,
      error: "NETWORK_ERROR",
    });
  }
});

// Verify OTP and complete login/registration
export const verifyOtpAndLogin = createAsyncThunk<
  { message: string; user: User },
  { userId: string; otpCode: string; type: "register" | "login" },
  { rejectValue: ApiError }
>("auth/verifyOtpAndLogin", async (payload, { rejectWithValue }) => {
  const endpoint =
    payload.type === "register"
      ? "/api/auth/register/verify-otp"
      : "/api/auth/login/verify-otp";
  try {
    const response = await apiClient.post(endpoint, {
      userId: payload.userId,
      otpCode: payload.otpCode,
    });
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return rejectWithValue(error.response.data as ApiError);
    }
    return rejectWithValue({
      success: false,
      message: `${payload.type === "register" ? "Registration" : "Login"} verification failed`,
      error: "NETWORK_ERROR",
    });
  }
});

// Logout user
export const logoutUser = createAsyncThunk<
  { success: boolean; message: string },
  void,
  { rejectValue: ApiError }
>("auth/logoutUser", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.post("/api/auth/logout");
    return {
      success: true,
      message: response.data.message || "Logged out successfully",
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return rejectWithValue(error.response.data as ApiError);
    }
    return rejectWithValue({
      success: false,
      message: "Logout failed",
      error: "NETWORK_ERROR",
    });
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
      state.errorCode = null;
      state.redirectTo = null;
    },
    setOtpSent: (state, action: PayloadAction<{ userId: string }>) => {
      state.otpSent = true;
      state.otpUserId = action.payload.userId;
      state.isLoading = false;
      state.error = null;
      state.errorCode = null;
      state.redirectTo = null;
    },
    resetOtpState: (state) => {
      state.otpSent = false;
      state.otpUserId = null;
    },
    clearUserSession: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.otpSent = false;
      state.otpUserId = null;
      state.error = null;
      state.errorCode = null;
      state.redirectTo = null;
      state.tokenExpiringWarning = false;

      // ✅ Effacer l'utilisateur dans Sentry
      clearSentryUser();
    },
    handleTokenExpiration: (state) => {
      // Called when frontend detects token is expired
      state.isAuthenticated = false;
      state.user = null;
      state.redirectTo = "/login";
      state.error = "Your session has expired. Please login again.";
      state.errorCode = "TOKEN_EXPIRED";

      // ✅ Effacer l'utilisateur dans Sentry
      clearSentryUser();
    },
    updateUserAfterStripeVerification: (
      state,
      action: PayloadAction<Partial<User>>,
    ) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Check Auth Status
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errorCode = null;
        state.redirectTo = null;
      })
      .addCase(
        checkAuthStatus.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.isLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload;
          state.tokenExpiringWarning = !!action.payload.tokenExpiringWarning;
          state.redirectTo = null;

          // ✅ Définir l'utilisateur dans Sentry
          setSentryUser({
            id: action.payload.id,
            email: action.payload.email,
            subscriptionPlan: action.payload.subscriptionPlan,
            subscriptionStatus: action.payload.subscriptionStatus,
          });
        },
      )
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload?.message || "Authentication check failed";
        state.errorCode = action.payload?.error || null;
        state.redirectTo = action.payload?.redirectTo || null;
      })

      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(
        fetchCurrentUser.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.isLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload;

          // ✅ Définir l'utilisateur dans Sentry
          setSentryUser({
            id: action.payload.id,
            email: action.payload.email,
            subscriptionPlan: action.payload.subscriptionPlan,
            subscriptionStatus: action.payload.subscriptionStatus,
          });
        },
      )
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        // Don't change authentication state here - we just failed to get details
        state.error = action.payload?.message || "Failed to fetch user profile";
        state.errorCode = action.payload?.error || null;
        // Check if we need to redirect due to auth issues
        if (
          action.payload?.error === "TOKEN_INVALID" ||
          action.payload?.error === "TOKEN_EXPIRED" ||
          action.payload?.error === "TOKEN_REVOKED"
        ) {
          state.isAuthenticated = false;
          state.redirectTo = action.payload?.redirectTo || "/login";
        }
      })

      // Request OTP (shared for login/register)
      .addCase(requestOtp.pending, (state) => {
        state.isLoading = true;
        state.otpSent = false;
        state.otpUserId = null;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(
        requestOtp.fulfilled,
        (state, action: PayloadAction<{ message: string; userId: string }>) => {
          state.isLoading = false;
          state.otpSent = true;
          state.otpUserId = action.payload.userId;
        },
      )
      .addCase(requestOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "OTP request failed";
        state.errorCode = action.payload?.error || null;
      })

      // Verify OTP and Login/Register
      .addCase(verifyOtpAndLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(
        verifyOtpAndLogin.fulfilled,
        (state, action: PayloadAction<{ message: string; user: User }>) => {
          state.isLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.otpSent = false;
          state.otpUserId = null;
          state.error = null;
          state.errorCode = null;

          // ✅ Définir l'utilisateur dans Sentry
          setSentryUser({
            id: action.payload.user.id,
            email: action.payload.user.email,
            subscriptionPlan: action.payload.user.subscriptionPlan,
            subscriptionStatus: action.payload.user.subscriptionStatus,
          });
        },
      )
      .addCase(verifyOtpAndLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Login/Registration failed";
        state.errorCode = action.payload?.error || null;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.errorCode = null;
        state.otpSent = false;
        state.otpUserId = null;

        // ✅ Effacer l'utilisateur dans Sentry
        clearSentryUser();
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Logout failed";
        state.errorCode = action.payload?.error || null;
        // Even if logout fails on the backend, clear the user session from the client
        state.user = null;
        state.isAuthenticated = false;
        state.otpSent = false;
        state.otpUserId = null;

        // ✅ Effacer l'utilisateur dans Sentry même en cas d'erreur
        clearSentryUser();
      });
  },
});

export const {
  clearAuthError,
  setOtpSent,
  resetOtpState,
  clearUserSession,
  handleTokenExpiration,
  updateUserAfterStripeVerification,
} = authSlice.actions;
export default authSlice.reducer;
